import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { getFileUrl } from '@/services/musicTracksService';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface TrackPlayerProps {
  storagePath: string;
  duration: number;
  className?: string;
  /** When true, only show play/pause button (no progress slider) */
  compact?: boolean;
  /** When true (with compact), do not fetch audio URL until user clicks play — use in lists to avoid loading many tracks */
  lazyLoad?: boolean;
  /** Storage path of the track that is currently allowed to play; others must pause */
  activeStoragePath?: string | null;
  /** Called when this player starts playing (so parent can set activeStoragePath) */
  onPlayStart?: () => void;
  /** Track ID — when provided, onTrackEnded is called when track finishes (for playCount) */
  trackId?: string;
  /** Called when track finishes playing (only if trackId provided). Use to increment playCount. */
  onTrackEnded?: (trackId: string) => void;
}

export function TrackPlayer({
  storagePath,
  duration,
  className,
  compact = false,
  lazyLoad = false,
  activeStoragePath = null,
  onPlayStart,
  trackId,
  onTrackEnded,
}: TrackPlayerProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!lazyLoad);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [playWhenReady, setPlayWhenReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onPlayStartRef = useRef(onPlayStart);
  const onTrackEndedRef = useRef(onTrackEnded);

  useEffect(() => {
    onPlayStartRef.current = onPlayStart;
  }, [onPlayStart]);
  useEffect(() => {
    onTrackEndedRef.current = onTrackEnded;
  }, [onTrackEnded]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setReady(false);
    setPlayWhenReady(false);
    if (!storagePath) {
      setAudioUrl(null);
      setLoading(false);
      return;
    }
    if (lazyLoad) {
      setAudioUrl(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    getFileUrl(storagePath)
      .then((url) => {
        setAudioUrl(url);
      })
      .catch(() => setAudioUrl(null))
      .finally(() => setLoading(false));
  }, [storagePath, lazyLoad]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    onPlayStartRef.current?.();
    audio.play().then(() => setIsPlaying(true)).catch(() => {});
  }, [audioUrl]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const handleSeek = useCallback((values: number[]) => {
    const seekTime = values[0] ?? 0;
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  }, []);

  useEffect(() => {
    if (activeStoragePath !== null && activeStoragePath !== storagePath && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [activeStoragePath, storagePath, isPlaying]);

  useEffect(() => {
    if (ready && playWhenReady) {
      onPlayStartRef.current?.();
      audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => {});
      setPlayWhenReady(false);
    }
  }, [ready, playWhenReady]);

  useEffect(() => {
    if (!audioUrl) return;

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (trackId) {
        onTrackEndedRef.current?.(trackId);
      }
    };
    const onCanPlay = () => setReady(true);
    const onError = () => setReady(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      audioRef.current = null;
    };
  }, [audioUrl, trackId]);

  const handleCompactClick = useCallback(() => {
    if (lazyLoad && !audioUrl && storagePath) {
      setLoading(true);
      getFileUrl(storagePath)
        .then((url) => {
          setAudioUrl(url);
          setPlayWhenReady(true);
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false));
      return;
    }
    togglePlay();
  }, [lazyLoad, audioUrl, storagePath, togglePlay]);

  if (compact) {
    const showPlayButton = lazyLoad && !audioUrl;
    const disabled = !showPlayButton && !ready;
    return (
      <div className={cn('flex items-center', className)}>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full"
          onClick={handleCompactClick}
          disabled={showPlayButton ? false : disabled}
        >
          {showPlayButton ? (
            loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4 ml-0.5" />
            )
          ) : !ready ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" />
          )}
        </Button>
      </div>
    );
  }

  if (loading || !audioUrl) {
    return (
      <div className={cn('flex items-center justify-center gap-2 py-4', className)}>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-sm text-muted-foreground">Could not load track</span>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0 rounded-full"
          onClick={togglePlay}
          disabled={!ready}
        >
          {!ready ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" />
          )}
        </Button>
        <div className="flex-1 min-w-0 space-y-2">
          <Slider
            value={[currentTime]}
            min={0}
            max={Math.max(duration, 1)}
            step={0.5}
            onValueChange={(v) => handleSeek(v)}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
            <span>{formatDuration(currentTime)}</span>
            <span>{formatDuration(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
